import type { PricingLogic } from "../schemas/upsell-constructs";

/**
 * Computes the effective unit price for an upsell after applying its
 * pricing logic against the product's base price.
 *
 * Shared between the widget (display + cart math) and the server (line
 * item discount allocation at order create time). Both must produce
 * identical results — never let the caller pass a price; always recompute
 * from the offer's pricing_logic.
 */
export function computeUpsellPrice(
  pricingLogic: PricingLogic,
  basePrice: number
): number {
  switch (pricingLogic.action) {
    case "fixed_price_per_item":
      return pricingLogic.amount;
    case "percentage_off":
      return Math.max(0, basePrice * (1 - pricingLogic.value / 100));
    case "fixed_amount_off":
      return Math.max(0, basePrice - pricingLogic.amount);
    case "none":
    default:
      return basePrice;
  }
}

/**
 * Computes the discounted unit price for a single line inside a bundle.
 *
 * Bundle pricing is line-aware: `fixed_amount_off` is interpreted as a
 * *proportional* reduction (`amount / bundleBasePrice`) applied to each
 * line, NOT a flat subtraction. A flat subtract would consume the full
 * bundle discount on every line and collapse the rest to zero/negative.
 *
 * Both the widget (display + cart math) and the server (the actual
 * charge) must call this so the customer-visible subtotal matches the
 * amount billed. Mirrors `computeUpsellPrice` in shape but is the only
 * correct entry point for bundle line pricing.
 *
 * Bundles never carry `fixed_price_per_item` or `none` (schema-enforced);
 * those branches fall through to `itemBasePrice` defensively.
 */
export function computeBundlePerItemUnit(
  pricingLogic: PricingLogic,
  itemBasePrice: number,
  bundleBasePrice: number
): number {
  switch (pricingLogic.action) {
    case "percentage_off":
      return Math.max(0, itemBasePrice * (1 - pricingLogic.value / 100));
    case "fixed_amount_off":
      if (bundleBasePrice <= 0) return itemBasePrice;
      return Math.max(
        0,
        itemBasePrice * (1 - pricingLogic.amount / bundleBasePrice)
      );
    case "fixed_price_per_item":
    case "none":
    default:
      return itemBasePrice;
  }
}

/**
 * Computes the per-pick unit price for a line in a *configurable* bundle.
 *
 * Configurable bundles distribute the bundled total equally across all
 * picks — picks within a group are interchangeable from the merchant's
 * perspective and don't carry per-option base prices. This means a
 * `pricing_logic = "none"` bundle (where the merchant typed a flat
 * `base_price`) is honored correctly: every pick costs `base_price /
 * totalPicks`, not the picked variant's catalog price.
 *
 * Mirror of `computeBundlePerItemUnit` but for configurable bundles —
 * both widget and server call this so the customer-visible per-pick
 * price matches the amount actually charged.
 */
export function computeBundlePerPickUnit(
  pricingLogic: PricingLogic,
  bundleBasePrice: number,
  totalPicks: number
): number {
  if (totalPicks <= 0) return 0;
  const bundledTotal = computeUpsellPrice(pricingLogic, bundleBasePrice);
  return Math.max(0, bundledTotal / totalPicks);
}

/**
 * Short label for the discount badge shown next to the upsell price.
 * Returns null when there's no meaningful discount to surface
 * (action: none, or fixed_price_per_item with no actual savings).
 */
export function getDiscountBadgeLabel(
  pricingLogic: PricingLogic,
  basePrice: number,
  currencyPrefix: string = "$"
): string | null {
  switch (pricingLogic.action) {
    case "percentage_off":
      return pricingLogic.value > 0 ? `${pricingLogic.value}% OFF` : null;
    case "fixed_amount_off":
      return pricingLogic.amount > 0
        ? `${currencyPrefix}${pricingLogic.amount.toFixed(2)} OFF`
        : null;
    case "fixed_price_per_item": {
      const savings = basePrice - pricingLogic.amount;
      return savings > 0 ? `${currencyPrefix}${savings.toFixed(2)} OFF` : null;
    }
    case "none":
    default:
      return null;
  }
}
